import { useEffect, useMemo, useState } from 'react';
import { Loader2, LibraryBig, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { premadeApi } from '../lib/api';

// Topics and Drills as tabs, to keep the list short (Leanne, 25 Oct 2026).
// Same tabs as the voice picker.
const GROUPS = [
    { key: 'situational', title: 'Situational topics' },
    { key: 'drill', title: 'Sound drills' },
    { key: 'chunk', title: 'Conversation chunks' },
];

// The premade collections, for adding after onboarding. The list, the phrase
// counts and the plan arithmetic all come from the server (/premade-collections),
// so a plan change or a new topic never needs a change here.
export default function PremadeCollectionsDialog({ open, onOpenChange, onAdded, onUpgrade }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [selected, setSelected] = useState([]);
    const [adding, setAdding] = useState(false);
    const [group, setGroup] = useState('situational');

    useEffect(() => {
        if (!open) return;
        setSelected([]);
        setLoading(true);
        premadeApi.list()
            .then(res => setData(res.data))
            .catch(() => toast.error('Could not load the pre-made collections.'))
            .finally(() => setLoading(false));
    }, [open]);

    const topics = data?.topics || [];
    const chosen = topics.filter(t => selected.includes(t.id));
    const phraseTotal = useMemo(() => chosen.reduce((n, t) => n + t.phrases, 0), [chosen]);
    const slotsLeft = data?.slots_left ?? 0;
    const overCap = phraseTotal > slotsLeft;

    const toggle = (id) => setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

    const handleAdd = async () => {
        if (!selected.length) return;
        setAdding(true);
        try {
            const res = await premadeApi.add(selected);
            toast.success(`Added ${res.data.collections} collection${res.data.collections === 1 ? '' : 's'} (${res.data.phrases} phrases)`);
            onOpenChange(false);
            onAdded?.(res.data);
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (err.response?.status === 402) {
                onOpenChange(false);
                onUpgrade?.(detail);
            } else {
                toast.error(detail || 'Could not add those collections.');
            }
        } finally {
            setAdding(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* overflow-x-hidden and tabs that can shrink: on a phone the two
                tab labels, held on one line, were wider than the card and it
                scrolled sideways (Leanne, 27 Oct 2026). */}
            <DialogContent className="bg-white max-h-[90vh] overflow-y-auto overflow-x-hidden p-0 gap-0" aria-describedby="premade-description">
                <DialogHeader className="p-5 pb-3">
                    <DialogTitle className="flex items-center gap-2"><LibraryBig className="h-5 w-5 text-primary" />Pre-made collections</DialogTitle>
                    <DialogDescription id="premade-description">
                        Collections of phrases ready to practise
                    </DialogDescription>
                </DialogHeader>

                <div className="px-5 pb-3 space-y-3">
                    <Tabs value={group} onValueChange={setGroup}>
                        <TabsList className="w-full h-auto">
                            {GROUPS.map(g => (
                                <TabsTrigger key={g.key} value={g.key}
                                             className="flex-1 min-w-0 whitespace-normal px-2 text-xs sm:text-sm leading-tight"
                                             data-testid={`premade-tab-${g.key}`}>
                                    {g.title}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                    {loading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
                    {!loading && GROUPS.filter(g => g.key === group).map(g => {
                        const items = topics.filter(t => t.kind === g.key);
                        if (!items.length) return null;
                        return (
                            <div key={g.key}>
                                <div className="space-y-2">
                                    {items.map(t => {
                                        const on = selected.includes(t.id);
                                        return (
                                            <label
                                                key={t.id}
                                                data-testid={`premade-${t.id}`}
                                                className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 transition-all ${
                                                    t.added
                                                        ? 'border-stone-100 bg-stone-50 opacity-70'
                                                        : on ? 'border-primary bg-primary/5 cursor-pointer' : 'border-stone-200 bg-white hover:border-stone-300 cursor-pointer'
                                                }`}
                                            >
                                                <Checkbox
                                                    className="mt-0.5"
                                                    checked={on}
                                                    disabled={t.added}
                                                    onCheckedChange={() => toggle(t.id)}
                                                />
                                                <span className="min-w-0 flex-1">
                                                    <span className="flex items-center justify-between gap-2">
                                                        <span className="min-w-0 break-words text-sm font-medium text-stone-800">{t.label}</span>
                                                        {t.added
                                                            ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600 flex-shrink-0"><Check className="h-3 w-3" />Added</span>
                                                            : <span className="text-xs text-stone-400 flex-shrink-0">{t.phrases} phrases</span>}
                                                    </span>
                                                    <span className="block break-words text-xs text-stone-500">{t.description}</span>
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="sticky bottom-0 border-t border-stone-100 bg-white p-4 space-y-2">
                    {selected.length > 0 && (
                        <p className={`text-xs ${overCap ? 'text-amber-700' : 'text-stone-500'}`}>
                            {selected.length} collection{selected.length === 1 ? '' : 's'}, {phraseTotal} phrases.{' '}
                            {overCap
                                ? `You have ${slotsLeft} phrase slot${slotsLeft === 1 ? '' : 's'} left on your plan.`
                                : `${slotsLeft - phraseTotal} slots left afterwards.`}
                        </p>
                    )}
                    <Button
                        data-testid="premade-add-btn"
                        className="w-full rounded-full bg-primary hover:bg-primary/80"
                        disabled={!selected.length || adding || loading}
                        onClick={handleAdd}
                    >
                        {adding ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" />Adding...</>) : (selected.length ? `Add ${selected.length} collection${selected.length === 1 ? '' : 's'}` : 'Choose collections to add')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
